// skipcq: JS-W1028
import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import BeerDuplicateCompareView from './BeerDuplicateCompareView';
import BeerDuplicateReviewQueueNav from './BeerDuplicateReviewQueueNav';
import AlertWidget from '../widgets/Alert';
import useBeerDuplicateReviewSession from '../../hooks/useBeerDuplicateReviewSession';
import useBeerIngestionReviewKeyboard from '../../hooks/useBeerIngestionReviewKeyboard';
import {
  captureDuplicateReviewNext,
  captureDuplicateReviewPrevious,
  captureDuplicateReviewView,
} from '../../utils/engageDuplicateReviewAnalytics';

export default function BeerDuplicateReviewDialog({
  open,
  reviewId,
  breweryId,
  onClose,
}) {
  const [activeReviewId, setActiveReviewId] = useState(reviewId);

  useEffect(() => {
    if (open && reviewId) {
      setActiveReviewId(reviewId);
    }
  }, [open, reviewId]);

  const handleDialogNavigate = useCallback((nextReviewId) => {
    if (!nextReviewId) {
      onClose();
      return;
    }
    setActiveReviewId(nextReviewId);
  }, [onClose]);

  const {
    activeReview,
    loading,
    error,
    actionsLoading,
    actionsError,
    clearError,
    showQueueNav,
    neighbors,
    handleApplySelected,
    handleCompleteReview,
    handleAddBeer,
    handlePrevious,
    handleNext,
  } = useBeerDuplicateReviewSession({
    reviewId: activeReviewId,
    breweryId,
    globalScope: false,
    onClose: handleDialogNavigate,
  });

  useEffect(() => {
    if (open && activeReviewId && activeReview?.breweryId) {
      captureDuplicateReviewView(activeReviewId, activeReview.breweryId);
    }
  }, [activeReview?.breweryId, activeReviewId, open]);

  const handlePreviousWithAnalytics = useCallback(() => {
    captureDuplicateReviewPrevious(activeReviewId, activeReview?.breweryId);
    handlePrevious();
  }, [activeReview?.breweryId, activeReviewId, handlePrevious]);

  const handleNextWithAnalytics = useCallback(() => {
    captureDuplicateReviewNext(activeReviewId, activeReview?.breweryId);
    handleNext();
  }, [activeReview?.breweryId, activeReviewId, handleNext]);

  useBeerIngestionReviewKeyboard({
    enabled: open && showQueueNav,
    onPrevious: handlePreviousWithAnalytics,
    onNext: handleNextWithAnalytics,
  });

  const handleCloseError = useCallback(() => {
    clearError();
  }, [clearError]);

  if (!open) {
    return null;
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
        <DialogTitle component="div">
          <Typography variant="h6" component="h2">
            Beer duplicate review
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <BeerDuplicateReviewQueueNav
            showQueueNav={showQueueNav}
            previousDisabled={!neighbors.previousReviewId}
            nextDisabled={!neighbors.nextReviewId}
            positionLabel={neighbors.positionLabel}
            onPrevious={handlePreviousWithAnalytics}
            onNext={handleNextWithAnalytics}
          />
          {error ? (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          ) : null}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} aria-label="Loading duplicate review" />
            </Box>
          ) : null}
          {!loading && activeReview ? (
            <BeerDuplicateCompareView
              reviewRow={activeReview}
              actionsDisabled={actionsLoading}
              onApplySelected={handleApplySelected}
              onCompleteReview={handleCompleteReview}
              onAddBeer={handleAddBeer}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={Boolean(actionsError)} autoHideDuration={6000} onClose={handleCloseError}>
        <AlertWidget onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {actionsError}
        </AlertWidget>
      </Snackbar>
    </>
  );
}

BeerDuplicateReviewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  reviewId: PropTypes.string,
  breweryId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

BeerDuplicateReviewDialog.defaultProps = {
  reviewId: null,
};
